import {
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  isPossiblePhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js/min'

/**
 * Phone numbers as the app stores them: a leading "+" and digits only
 * ("+15045551234"). Phone fields pick the country and take the local number
 * (components/kb/PhoneInput.vue); these helpers parse, check and format with
 * libphonenumber's metadata.
 */

export type { CountryCode }

export const DEFAULT_PHONE_COUNTRY: CountryCode = 'US'

const digitsOf = (value: string | null | undefined) => (value ?? '').replace(/\D/g, '')

/**
 * Normalize what someone typed to the stored form. A bare 10-digit number is
 * taken as US/Canada and gets a +1; anything else keeps its own country code.
 * Empty input (or only a "+" / "+1" prefix) becomes ''.
 */
export function normalizePhone(value: string | null | undefined): string {
  const digits = digitsOf(value)
  if (!digits || digits === '1') return ''
  if (digits.length === 10 && !(value ?? '').trim().startsWith('+')) return `+1${digits}`
  return `+${digits}`
}

/** A complete number: the right number of digits for its country code. */
export function isValidPhone(value: string | null | undefined): boolean {
  const stored = normalizePhone(value)
  return Boolean(stored) && isPossiblePhoneNumber(stored)
}

/** Readable form for display: "+1 (504) 555-1234" for US/Canada, "+44 20 7946 0958" elsewhere. */
export function formatPhone(value: string | null | undefined): string {
  const stored = normalizePhone(value)
  if (!stored) return ''
  const parsed = parsePhoneNumberFromString(stored)
  if (!parsed) return stored
  const national = parsed.nationalNumber
  if (parsed.countryCallingCode === '1' && national.length === 10) {
    return `+1 (${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`
  }
  return parsed.formatInternational()
}

// Calling codes several countries share resolve to the main one
const MAIN_COUNTRY: Record<string, CountryCode> = { 1: 'US', 7: 'RU', 44: 'GB', 47: 'NO', 61: 'AU', 39: 'IT', 212: 'MA', 262: 'RE', 290: 'SH', 358: 'FI', 590: 'GP', 599: 'CW' }

function countryForCallingCode(code: string): CountryCode | undefined {
  return MAIN_COUNTRY[code] ?? getCountries().find(country => getCountryCallingCode(country) === code)
}

/**
 * A stored number as the phone field shows it: its country and the local
 * part. A number whose country code isn't known keeps all of it ("+999…") so
 * editing doesn't silently put another code in front.
 */
export function splitPhone(
  value: string | null | undefined,
  fallback: CountryCode = DEFAULT_PHONE_COUNTRY,
): { country: CountryCode, national: string } {
  const stored = normalizePhone(value)
  if (!stored) return { country: fallback, national: '' }
  const parsed = parsePhoneNumberFromString(stored)
  const country = parsed && (parsed.country ?? countryForCallingCode(parsed.countryCallingCode))
  if (!parsed || !country) return { country: fallback, national: stored }
  return { country, national: parsed.formatNational() }
}

/**
 * The stored form of a local number typed for a country. A trunk prefix is
 * dropped (UK "020 …" is +44 20 …); a number typed with "+" keeps its own
 * country code.
 */
export function joinPhone(country: CountryCode, typed: string | null | undefined): string {
  const text = (typed ?? '').trim()
  if (text.startsWith('+')) return normalizePhone(text)
  const digits = digitsOf(text)
  if (!digits) return ''
  const parsed = parsePhoneNumberFromString(text, country)
  return parsed?.number ?? `+${getCountryCallingCode(country)}${digits}`
}

/** The country a number typed with "+" belongs to, if it can tell. */
export function countryOfTyped(typed: string): CountryCode | undefined {
  const parsed = parsePhoneNumberFromString(typed.trim())
  return parsed && (parsed.country ?? countryForCallingCode(parsed.countryCallingCode))
}

export interface PhoneCountry {
  code: CountryCode
  name: string
  dial: string
}

let countryList: PhoneCountry[] | null = null

/** Every country with its calling code, by name. */
export function phoneCountries(): PhoneCountry[] {
  if (countryList) return countryList
  const names = new Intl.DisplayNames(['en'], { type: 'region' })
  countryList = getCountries()
    .map(code => ({ code, name: names.of(code) ?? code, dial: `+${getCountryCallingCode(code)}` }))
    .sort((a, b) => a.name.localeCompare(b.name))
  return countryList
}

export function dialCode(country: CountryCode): string {
  return `+${getCountryCallingCode(country)}`
}
